import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { api } from '../services/api';

export default function BlockedNumbers() {
  const [open, setOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [reason, setReason] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const queryClient = useQueryClient();

  const { data: blockedNumbers, isLoading } = useQuery(
    'blockedNumbers',
    () => api.getBlockedNumbers({ limit: 100, offset: 0 })
  );

  const addMutation = useMutation(
    (data) => api.addBlockedNumber(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('blockedNumbers');
        setOpen(false);
        setPhoneNumber('');
        setReason('');
        setSnackbar({
          open: true,
          message: 'Number blocked successfully',
          severity: 'success',
        });
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: `Error: ${error.response?.data?.error || error.message}`,
          severity: 'error',
        });
      },
    }
  );

  const deleteMutation = useMutation(
    (phoneNumber) => api.removeBlockedNumber(phoneNumber),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('blockedNumbers');
        setSnackbar({
          open: true,
          message: 'Number unblocked successfully',
          severity: 'success',
        });
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: `Error: ${error.response?.data?.error || error.message}`,
          severity: 'error',
        });
      },
    }
  );

  const handleAdd = () => {
    addMutation.mutate({
      phoneNumber,
      reason,
      category: 'manual',
    });
  };

  const handleDelete = (phoneNumber) => {
    if (window.confirm(`Are you sure you want to unblock ${phoneNumber}?`)) {
      deleteMutation.mutate(phoneNumber);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Add Number
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Phone Number</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Added By</TableCell>
                <TableCell>Date Added</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {blockedNumbers?.map((number) => (
                <TableRow key={number.phone_number}>
                  <TableCell>{number.phone_number}</TableCell>
                  <TableCell>{number.reason}</TableCell>
                  <TableCell>{number.category}</TableCell>
                  <TableCell>{number.added_by}</TableCell>
                  <TableCell>
                    {format(new Date(number.created_at), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(number.phone_number)}
                      disabled={deleteMutation.isLoading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Block Phone Number</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Phone Number"
            fullWidth
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+1234567890"
          />
          <TextField
            margin="dense"
            label="Reason"
            fullWidth
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            disabled={!phoneNumber || addMutation.isLoading}
          >
            Block
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
